import { useEffect, useState } from 'react';
import { Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import { Add, DeleteOutline } from '@mui/icons-material';
import { useTranslations } from '@/i18n/use-translations';
import type { TranslationKey } from '@/i18n/translation-key';
import {
  useOrgConfidencePresetsQuery,
  useSaveOrgConfidencePresetsMutation,
} from '@/state/queries/player/use-confidence';

export function OrganizationConfidencePresetsPanel({ organizationId }: { organizationId: string }) {
  const t = useTranslations();
  const { data } = useOrgConfidencePresetsQuery(organizationId);
  const save = useSaveOrgConfidencePresetsMutation();
  const [presets, setPresets] = useState<{ id?: string; text: string }[]>([]);

  useEffect(() => {
    if (data?.presets) setPresets(data.presets);
  }, [data?.presets]);

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        {t('organization.settingsMenu.confidenceIntro' as TranslationKey)}
      </Typography>
      {presets.map((preset, index) => (
        <Stack key={preset.id ?? index} direction="row" spacing={1}>
          <TextField
            fullWidth
            size="small"
            value={preset.text}
            onChange={(event) =>
              setPresets((current) =>
                current.map((item, itemIndex) => (itemIndex === index ? { ...item, text: event.target.value } : item)),
              )
            }
          />
          <IconButton
            aria-label={t('common.delete' as TranslationKey)}
            onClick={() => setPresets((current) => current.filter((_, itemIndex) => itemIndex !== index))}
          >
            <DeleteOutline />
          </IconButton>
        </Stack>
      ))}
      <Stack direction="row" spacing={1}>
        <Button startIcon={<Add />} onClick={() => setPresets((current) => [...current, { text: '' }])}>
          {t('organization.settingsMenu.confidenceAdd' as TranslationKey)}
        </Button>
        <Button
          variant="contained"
          disabled={save.isPending}
          onClick={() => void save.mutateAsync({ organizationId, presets })}
        >
          {t('common.save' as TranslationKey)}
        </Button>
      </Stack>
    </Stack>
  );
}
